/*
 * Basic file with global javascripts.
 * Files imported here will be available on every page.
 */

import '~/styles/styles.scss';
import 'swiper/css/effect-fade';
import '../../utilities/lazysizes';
import '../../utilities/swiper';

import Alpine from 'alpinejs';

// Algolia implementation
import algoliaGlobalSearch from '~/js/bundles/sections/algolia-global-search';
import algoliaBrowser from '~/js/bundles/sections/algolia-browser';
import algoliaFacetItem from '~/js/bundles/snippets/algoliaFacetItem';

import headerComponent from '~/js/bundles/sections/header';
import heroCarousel from '~js/bundles/sections/hero-carousel';
import collectionsExplorerInit from '~js/bundles/sections/collections-explorer';
import countdownTimerInit from '~/js/bundles/snippets/countdown-timer';
import countdownTimerAnnouncementInit from '~/js/bundles/snippets/countdown-timer-announcement';
import quickLinks from '~js/bundles/sections/quick-links';
import productExplorerInit from '~/js/bundles/sections/product-explorer';
import scrollTrigger from '~js/bundles/shared/alpine-scroll-trigger';
import globalSearch from '~js/bundles/sections/global-search';
import productCard from '~js/bundles/snippets/product-card';
import {
  navStore,
  navDesktopComponent,
  navMobileComponent,
} from '../snippets/navigation';
import desktopNavBackdrop from '~js/bundles/layout/desktop-nav-backdrop';
import { cartStore, cartComponent } from '~js/bundles/snippets/cart';
import giftBoxDrawer from '~js/bundles/snippets/gift-box-drawer';
import lineItemComponent from '~js/bundles/snippets/line-item';
import quantitySelector from '../snippets/quantity-selector';

import subscribeEmailSignupInit from '../snippets/subscribe-email-signup';
import addToWishlistButton from '../snippets/add-to-wishlist-button';
import productsBrowser from '../sections/products-browser/products-browser';
import productsBrowserProduct from '../snippets/products-browser-product';
import productsBrowserFacet from '../snippets/products-browser-facet';
import productsBrowserFacetSlider from '../snippets/products-browser-facet-slider';
import drawer from '../snippets/drawer';
import productsBrowserFacetsWrapper from '../snippets/products-browser-facets-wrapper';
import quickAddDrawer from '../snippets/quick-add-drawer';
import quickAddDrawerWrapper from '../snippets/quick-add-drawer-wrapper';
import quickAddDrawerProductGallery from '../snippets/quick-add-drawer-product-gallery';
import productsBrowserFacetSpecial from '../snippets/products-browser-facet-special';
import productsBrowserSort from '../snippets/products-browser-sort';
import recommendedProducts from '~/js/bundles/snippets/recommended-products';
import paginatedCollections from '~js/bundles/sections/paginated-collections';
import paginatedPrintCollections from '~js/bundles/sections/paginated-print-collections';
import imageReviewCarousel from '~js/bundles/sections/image-review-carousel';
import heroProduct from '../sections/hero-product';
import videoLibraryPlayer from '~js/bundles/sections/video-library-player';
import videoLibraryItem from '~js/bundles/snippets/video-library-item';

import xOnShopify from '~/js/alpine-plugins/directives/x-on-shopify';
import heroProductPrints from '../snippets/hero-product-prints';
import heroProductGallery from '../snippets/hero-product-gallery';
import heroProductAccordion from '../snippets/hero-product-accordion';
import { productData } from '../snippets/product-variant-selection';
import heroProductAdd from '../snippets/hero-product-add';
import heroProductInfo from '../snippets/hero-product-info';
import heroProductGalleryModal from '../snippets/hero-product-gallery-modal';
import heroSearch from '../sections/hero-search';
import cartDrawer from '../snippets/cart-drawer';
import cartSection from '../sections/cart-section';
import accordionSection from '../sections/accordion-section';
import customerAddressForm from '../sections/customer-address-form';
import reviewStars from '../snippets/review-stars';
import { initChatDetection } from '~/js/utilities/chat-detection';

import bundleBuilder from '~/js/bundles/sections/bundle-builder';
import bundleBuilderGallery from '~/js/bundles/snippets/bundle-builder-product-gallery';
import bundleBuilderForm from '~/js/bundles/snippets/bundle-builder-form';

import lottiePlayer from '~/js/bundles/snippets/lottie-player';
import { initAlgoliaTracking } from '~/js/utilities/algolia/tracking';
import { initSentryTracking } from '~/js/utilities/sentry';

const navStoreInstance = navStore();
Alpine.store('nav', navStoreInstance);

const cartStoreInstance = cartStore();
Alpine.store('cart', cartStoreInstance);
window.cartStore = cartStoreInstance;

Alpine.data('heroCarousel', heroCarousel);
Alpine.data('subscribeEmailSignup', subscribeEmailSignupInit);
Alpine.data('productExplorer', productExplorerInit);
Alpine.data('collectionsExplorer', collectionsExplorerInit);
Alpine.data(
  'countdownTimer',
  (
    launchDateYear,
    launchDateMonth,
    launchDateDay,
    launchDateHour,
    launchDateMinute = 0,
    launchStatusOverride
  ) =>
    countdownTimerInit({
      launchDateYear,
      launchDateMonth,
      launchDateDay,
      launchDateHour,
      launchDateMinute,
      launchStatusOverride,
    })
);

Alpine.data(
  'countdownTimerAnnouncement',
  (
    launchDateYear,
    launchDateMonth,
    launchDateDay,
    launchDateHour,
    launchDateMinute = 0,
    launchStatusOverride
  ) =>
    countdownTimerAnnouncementInit({
      launchDateYear,
      launchDateMonth,
      launchDateDay,
      launchDateHour,
      launchDateMinute,
      launchStatusOverride,
    })
);

Alpine.data('headerComponent', headerComponent);

Alpine.data('algoliaGlobalSearch', algoliaGlobalSearch);
Alpine.data('algoliaBrowser', algoliaBrowser);
Alpine.data('algoliaFacetItem', algoliaFacetItem);

Alpine.data('addToWishlistButton', addToWishlistButton);
Alpine.data('scrollTrigger', scrollTrigger);
Alpine.data('productsBrowser', productsBrowser);
Alpine.data('productsBrowserProduct', productsBrowserProduct);
Alpine.data('productsBrowserFacet', productsBrowserFacet);
Alpine.data('productsBrowserFacetSlider', productsBrowserFacetSlider);
Alpine.data('productsBrowserFacetsWrapper', productsBrowserFacetsWrapper);
Alpine.data('globalSearch', globalSearch);
Alpine.data('productCard', productCard);
Alpine.data('drawer', drawer);
Alpine.data('quickLinks', quickLinks);
Alpine.data('navDesktop', navDesktopComponent);
Alpine.data('navMobile', navMobileComponent);
Alpine.data('desktopNavBackdrop', desktopNavBackdrop);
Alpine.data('cartComponent', cartComponent);
Alpine.data('lineItemComponent', lineItemComponent);
Alpine.data('quantitySelector', quantitySelector);
Alpine.data('quickAddDrawer', quickAddDrawer);
Alpine.data('quickAddDrawerWrapper', quickAddDrawerWrapper);
Alpine.data('quickAddDrawerProductGallery', quickAddDrawerProductGallery);
Alpine.data('productsBrowserFacetSpecial', productsBrowserFacetSpecial);
Alpine.data('productsBrowserSort', productsBrowserSort);
Alpine.data('recommendedProducts', recommendedProducts);
Alpine.data('paginatedCollections', paginatedCollections);
Alpine.data('paginatedPrintCollections', paginatedPrintCollections);
Alpine.data('imageReviewCarousel', imageReviewCarousel);
Alpine.data('giftBoxDrawer', giftBoxDrawer);
Alpine.data('heroProduct', heroProduct);
Alpine.data('heroProductPrints', heroProductPrints);
Alpine.data('heroProductGallery', heroProductGallery);
Alpine.data('heroProductAccordion', heroProductAccordion);
Alpine.data('productData', productData);
Alpine.data('heroProductAdd', heroProductAdd);
Alpine.data('heroProductInfo', heroProductInfo);
Alpine.data('heroProductGalleryModal', heroProductGalleryModal);
Alpine.data('videoLibraryPlayer', videoLibraryPlayer);
Alpine.data('videoLibraryItem', videoLibraryItem);
Alpine.data('heroSearch', heroSearch);
Alpine.data('cartDrawer', cartDrawer);
Alpine.data('cartSection', cartSection);
Alpine.data('customerAddressForm', customerAddressForm);
Alpine.data('reviewStars', reviewStars);
Alpine.data('lottiePlayer', lottiePlayer);
Alpine.data('accordionSection', accordionSection);
Alpine.data('bundleBuilder', bundleBuilder);
Alpine.data('bundleBuilderProductGallery', bundleBuilderGallery);
Alpine.data('bundleBuilderForm', bundleBuilderForm);

Alpine.plugin(xOnShopify);

window.Alpine = Alpine;
Alpine.start();
initChatDetection();
initAlgoliaTracking();
initSentryTracking();

// BUGHERD
if (
  window.location.hostname !== '127.0.0.1' &&
  !window.Shopify.designMode &&
  window.Shopify.theme.role !== 'main'
) {
  const script = document.createElement('script');
  script.src =
    'https://www.bugherd.com/sidebarv2.js?apikey=zu6rir2qmqhqw3stuuc4bq';
  document.head.append(script);
}
